/* eslint-disable indent */
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import { PROFILE_COMPLETION_FIELDS_NAME, QUIZ_NAMES } from '../constants'
import { isEmpty } from 'lodash'

/**
 * Hook to get the form fields, values and errors for the profile completion form
 *
 */

export const useProfileCompletion = () => {
	/**
	 * Function generate the placeholder used for select, input or more type.
	 *
	 *  Note 28 Aug 2024: The rest questionName that not implemented yet will return 'Select an option' due to the Schema and Design
	 *  don't have the information about the questionName
	 *
	 *  @inheritDoc - https://docs.google.com/spreadsheets/d/1J5ESLyuZHvE027yUP7bgnG8Uz3RrulIY8XI0oZucy10/edit?pli=1&gid=1142353116#gid=1142353116
	 *
	 * @param quizName - The name of the quiz to generate the placeholder for variants kind of user's profile
	 * */
	const generatePlaceholder = ({ questionName }) => {
		switch (questionName) {
			case PROFILE_COMPLETION_FIELDS_NAME.COUNTRY:
				return 'Select your country of residence'
			case PROFILE_COMPLETION_FIELDS_NAME.COUNTRY_OF_BIRTH:
				return 'Select your country of birth'
			case PROFILE_COMPLETION_FIELDS_NAME.DATE_OF_ORIGINAL_LICENSE:
				return 'Select approximate date you received your RN license'
			case PROFILE_COMPLETION_FIELDS_NAME.SPECIALTY:
				return 'Select Speciality'
			case PROFILE_COMPLETION_FIELDS_NAME.OTHER_NURSE_LICENSE_COUNTRIES:
				return 'Select countries you have a RN license in'
			case PROFILE_COMPLETION_FIELDS_NAME.DISCIPLINE:
				return 'Select your Unit'
			case PROFILE_COMPLETION_FIELDS_NAME.YEAR_IN_CURRENT_UNIT:
			case PROFILE_COMPLETION_FIELDS_NAME.YEARS_OF_TOTAL_EXPERIENCE:
				return 'Enter your years of experience'
			case PROFILE_COMPLETION_FIELDS_NAME.PROFESSION:
				return 'E.g. Burn ICU'
			case PROFILE_COMPLETION_FIELDS_NAME.PHONE:
				return '(+00) 0000 0000'
			case PROFILE_COMPLETION_FIELDS_NAME.WHATSAPP_PHONE:
				return 'Enter your WhatsApp Number starting with + and Country Code'
			case PROFILE_COMPLETION_FIELDS_NAME.EDUCATION_LEVEL:
				return 'Select your highest level of education'
			case PROFILE_COMPLETION_FIELDS_NAME.SCHOOL_NAME:
				return 'Enter the school’s name'
			case PROFILE_COMPLETION_FIELDS_NAME.REFER_RELOCATION_STATES:
				return 'Select your preferred relocation locations'
			case PROFILE_COMPLETION_FIELDS_NAME.NCLEX_TEST_DATE:
			case PROFILE_COMPLETION_FIELDS_NAME.EXAM_PASS_YEAR:
				return 'Select date'
			case PROFILE_COMPLETION_FIELDS_NAME.COUNTRY_OF_ORIGINAL_LICENSE:
				return 'Select country in which RN license was issued'
			case PROFILE_COMPLETION_FIELDS_NAME.VISA_TYPE_VISITED:
				return 'Select your Visa type'
			case PROFILE_COMPLETION_FIELDS_NAME.EXPECTED_OPT_DATE:
				return 'Select date you expect OPT to begin'
			case PROFILE_COMPLETION_FIELDS_NAME.PRIORITY_DATE:
				return 'Select your priority date'
			case PROFILE_COMPLETION_FIELDS_NAME.VISA_EXPIRATION_DATE:
				return 'Select your current Visa expiration date'
			case PROFILE_COMPLETION_FIELDS_NAME.ATT_EXPIRATION_DATE:
				return 'Select date your Authorization to Test (ATT) expiration date'
			case PROFILE_COMPLETION_FIELDS_NAME.VISA_TYPE:
				return 'Select your current Visa type'
			case PROFILE_COMPLETION_FIELDS_NAME.NCLEX_STATE:
				return 'Select your registered board of nursing'
			case PROFILE_COMPLETION_FIELDS_NAME.SCHEDULED_DATE:
				return 'Select date you scheduled for your NCLEX exam'
			case PROFILE_COMPLETION_FIELDS_NAME.NEEDS_SPONSORSHIP:
			case PROFILE_COMPLETION_FIELDS_NAME.RESUME:
			case PROFILE_COMPLETION_FIELDS_NAME.BESIDE:
			case PROFILE_COMPLETION_FIELDS_NAME.BACK_TO_BEDSIDE_NURSING:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_FOREIGN_NURSE_LICENSE:
			case PROFILE_COMPLETION_FIELDS_NAME.VISITED_US:
			case PROFILE_COMPLETION_FIELDS_NAME.UNLAWFUL_STATUS:
			case PROFILE_COMPLETION_FIELDS_NAME.EMPLOYMENT_PETITION_FILED:
			case PROFILE_COMPLETION_FIELDS_NAME.HAVE_PLAN_OPT:
			case PROFILE_COMPLETION_FIELDS_NAME.SCHEDULED_EXAM:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_ATT:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_BSC_MEDICAL_LAB:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_AMT_OT_ASCP_CERTIFICATION:
			case PROFILE_COMPLETION_FIELDS_NAME.AMT_OR_ASCP_ATTACHMENT:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_STARTED_VISA_SCREENING:
			case PROFILE_COMPLETION_FIELDS_NAME.VISA_SCREENING_OR_ENGLISH_EXAM_DOCUMENT:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_FPGC:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_NPTE:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_PTAL:
			case PROFILE_COMPLETION_FIELDS_NAME.HAS_NBCOT:
			default:
				return 'Select an option'
		}
	}

	/**
	 * Calculate the percentage of the profile completion based on `quizName`
	 * */
	const calcPercentage = (quizName) => {
		switch (quizName) {
			case QUIZ_NAMES.NURSE_LICENSE:
			case QUIZ_NAMES.NURSE_LICENSE_WITHOUT_NCLEX:
				return 20

			case QUIZ_NAMES.NURSE_EXPERIENCE:
			case QUIZ_NAMES.NURSE_EXPERIENCE_WITHOUT_NCLEX:
			case QUIZ_NAMES.MEDICAL_TECHNOLOGIST_EXPERIENCE:
			case QUIZ_NAMES.OTHER_EDU:
			case QUIZ_NAMES.VET_EDU:
			case QUIZ_NAMES.RAD_TECH_EDU:
			case QUIZ_NAMES.RESPIRATORY_THERAPIST_EDU:
				return 40

			case QUIZ_NAMES.PHARMACIST_EDU:
			case QUIZ_NAMES.OCCUPATIONAL_THERAPY_EDU:
			case QUIZ_NAMES.PHYSICAL_THERAPY_EDU:
			case QUIZ_NAMES.NURSE_AID_CONTACT:
				return 50

			case QUIZ_NAMES.NURSE_RELOCATION:
			case QUIZ_NAMES.NURSE_RELOCATION_WITHOUT_NCLEX:
				return 60

			case QUIZ_NAMES.MEDICAL_TECHNOLOGIST_CONTACT:
			case QUIZ_NAMES.OTHER_CONTACT:
			case QUIZ_NAMES.VET_CONTACT:
			case QUIZ_NAMES.RAD_TECH_CONTACT:
			case QUIZ_NAMES.RESPIRATORY_THERAPIST_CONTACT:
				return 70

			case QUIZ_NAMES.NURSE_IN_US:
			case QUIZ_NAMES.NURSE_IN_US_WITHOUT_NCLEX:
				return 80

			default:
				// !!! DO NOT REMOVE THOSE COMMENT BELOW !!!
				// case QUIZ_NAMES.NURSE_GENERAL:
				// case QUIZ_NAMES.NURSE_GENERAL_WITHOUT_NCLEX:
				// case QUIZ_NAMES.MEDICAL_TECHNOLOGIST_GENERAL:
				// case QUIZ_NAMES.NURSE_AID_GENERAL:
				// case QUIZ_NAMES.PHARMACIST_GENERAL:
				// case QUIZ_NAMES.OCCUPATIONAL_THERAPY_GENERAL:
				// case QUIZ_NAMES.RAD_TECH_GENERAL:
				// case QUIZ_NAMES.RESPIRATORY_THERAPIST_GENERAL:
				// case QUIZ_NAMES.VET_GENERAL:
				// case QUIZ_NAMES.OTHER_GENERAL:
				return 0
		}
	}

	function removeUndefinedKey(obj) {
		if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
			const { undefined: _, ...rest } = obj
			return rest
		}
		return obj
	}

	const formatValues = (values) => {
		if (!values) return []

		// Remove the all the fields that are [key] === undefined
		const filteredValues = removeUndefinedKey(values) || []

		if (isEmpty(filteredValues)) return []

		if (typeof filteredValues === 'object') {
			return Object.entries(filteredValues)
				.map(([name, answer]) => ({
					name,
					answer,
					_destroy: false,
				}))
				.filter(({ name }) => name !== 'acceptTermAndPolicy')
		}

		return filteredValues
			?.map(([name, answer]) => ({
				name,
				answer,
				_destroy: false,
			}))
			.filter(({ name }) => name !== 'acceptTermAndPolicy')
	}

	const isLastQuiz = (quizName) => {
		const LAST_QUIZ_NAMES = [
			QUIZ_NAMES.NURSE_IN_US,
			QUIZ_NAMES.NURSE_IN_US_WITHOUT_NCLEX,
			QUIZ_NAMES.MEDICAL_TECHNOLOGIST_CONTACT,
			QUIZ_NAMES.NURSE_AID_CONTACT,
			QUIZ_NAMES.PHARMACIST_EDU,
			QUIZ_NAMES.PHYSICAL_THERAPY_EDU,
			QUIZ_NAMES.OCCUPATIONAL_THERAPY_EDU,
			QUIZ_NAMES.RAD_TECH_CONTACT,
			QUIZ_NAMES.RESPIRATORY_THERAPIST_CONTACT,
			QUIZ_NAMES.OTHER_CONTACT,
			QUIZ_NAMES.VET_CONTACT,
			QUIZ_NAMES.UNLAWFUL_STATUS,
			QUIZ_NAMES.NURSE_RELOCATION_WITHOUT_NCLEX,
		]

		return LAST_QUIZ_NAMES.includes(quizName)
	}

	const isPhoneField = (name) => {
		if (name === PROFILE_COMPLETION_FIELDS_NAME.PHONE || name === PROFILE_COMPLETION_FIELDS_NAME.WHATSAPP_PHONE) return true
		return false
	}

	const isOptionalField = (questionName) => {
		const OPTIONALS = [PROFILE_COMPLETION_FIELDS_NAME.WHATSAPP_PHONE, PROFILE_COMPLETION_FIELDS_NAME.VISA_SCREENING_OR_ENGLISH_EXAM_DOCUMENT]

		return OPTIONALS.includes(questionName)
	}

	return { generatePlaceholder, calcPercentage, formatValues, isLastQuiz, isPhoneField, isOptionalField }
}

// Custom hook to check if form is dirty, excluding specific fields
export const useCustomDirtyCheck = (excludeFields = []) => {
	const { values, initialValues } = useFormikContext()

	const [isCustomDirty, setIsCustomDirty] = useState(false)

	useEffect(() => {
		const isDirty = Object.keys(values).some((key) => {
			if (excludeFields.includes(key)) return false
			return values[key] !== initialValues[key]
		})
		setIsCustomDirty(isDirty)
	}, [values, initialValues, excludeFields])

	return isCustomDirty
}

const prepareDataBeforeSubmit = (filteredValues, currentQuizAnswers = []) => {
	return filteredValues
		.map(({ answer }) => {
			if (!answer) return null
			const answered = currentQuizAnswers.find(({ quizQuestionId: answeredQuizQsId }) => answer?.quizQuestionId === answeredQuizQsId)

			// Currently, I have no idea about `blobId` , just keep it here
			const { answer: value, files, _destroy, id } = answer

			// Prevent un-expected accident when binding incorrect value to the field
			const answeredId = answered?.id || (id === answer?.quizQuestionId ? null : id)

			const remappedAnswer = {
				id: answeredId,
				answer: typeof answer === 'object' ? value : answer,
				quizQuestionId: answer?.quizQuestionId,
				_destroy,
			}

			remappedAnswer.files = files?.map(({ fileSignedBlobId, _destroy, id }) => ({ id, fileSignedBlobId, _destroy })) || []

			return remappedAnswer
		})
		.filter((value) => value && value?.answer !== undefined && value?.answer !== null) // Allow answer empty string
}

/**
 * Format the values of the form before submit
 *
 * NOTE: This function NO LONGER uses DOM queries.
 * For submitSingleAnswer: We're submitting a single answer that the user just changed,
 * so it's guaranteed to be visible (no need to filter by DOM).
 *
 * @param {Array} values - Array of { answer: {...} } objects
 * @param {Array} currentQuizAnswers - Current answers from store (for getting existing IDs)
 * @param {Function} getDestroyQuestions - Optional callback (deprecated, not used with single answer submit)
 * @returns {Array} - The formatted values ready for API submission
 * */
export const useFormatValues = (values, currentQuizAnswers = [], getDestroyQuestions) => {
	if (!values) return []

	// For submitSingleAnswer: values contains only the single answer being submitted
	// No need to filter by DOM - the answer is from a field the user just interacted with
	// which means it's definitely visible/rendered
	const filteredValues = values.filter(({ answer }) => answer?.quizQuestionId)

	// getDestroyQuestions callback is deprecated for single answer submit
	// Destroy logic is handled separately via branching auto-destroy in form component
	if (getDestroyQuestions) {
		getDestroyQuestions(() => [])
	}

	// If the user re-answers the question, we need to update the answers by add the field `id` to the answer
	// The `id` field is from `currentQuizAnswers[index].id`
	// If the user `answers` the question for the first time, we need to set the `id` to `null`
	// returns the list of `answers`
	// `answers` contain fields: `id`, `quizQuestionId`, `answer`, _destroy
	return prepareDataBeforeSubmit(filteredValues, currentQuizAnswers)
}

export const renderBackButton = (previousStep, currentQuizName) => {
	// 1st condition
	if (!previousStep) return false
	// If the current quiz is the first quiz, hide the back button
	if (String(currentQuizName).includes('_general')) return false

	return true
}
